import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Zap, Sparkles } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FeatureChipsInput } from "@/components/FeatureChipsInput";
import { useShowDbFieldNames, DbFieldBadge } from "@/hooks/useShowDbFieldNames";
import { getCapturedPropertyDetails, mergeCapturedPropertyDetails } from "@/utils/capturedPropertyCookie";
import { useShopperAuth } from "@/context/ShopperAuthContext";

const API = getApiUrl();

const SECTION_LABELS = {
  contact: "User Details",
  property: "Property Details",
  address: "Property Address",
  price: "Price",
  agent: "Agent Details",
  assets: "Assets",
  features: "Features",
  seo: "SEO",
  listing_status: "Listing Status",
  property_status: "Property Status",
  other: "Other",
};

// Falls back to a human-readable label when the CMS field's label equals its
// raw key (e.g. "cms_House_Name_Number" -> "House Name Number") — this is a
// PUBLIC-facing form, never show internal CMS key formatting to visitors.
function humaniseLabel(field) {
  if (field.label && field.label !== field.key) return field.label;
  return field.key
    .replace(/^cms_/, "")
    .replace(/_/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Public "List your property" capture form. Renders whichever CMS fields
 * the admin has ticked "Include" for on CMS → Fields (grouped into
 * Property / Address / Price sections), plus Name/Email/Phone (always
 * required — the email is where the claim link is sent).
 *
 * On submit → POST /list-property/submit, which creates an unclaimed
 * `properties` doc and emails a claim link. See routes/list_property.py.
 *
 * Signed-in agents get a different path: as soon as they've typed a
 * postcode or title, POST /list-property/draft creates the property
 * immediately (unpublished, already owned by their account) so it shows
 * up in My Properties right away; further changes debounce-PATCH
 * /list-property/draft/{id}. Anonymous visitors get a 401 on the first
 * attempt and fall back to the original one-shot Submit flow untouched.
 *
 * Config:
 *   - heading, subheading, submit_button_text
 *   - success_heading, success_message, success_message_2
 *   - redirect_slug: optional page path (e.g. "/thank-you") to send the
 *     visitor to immediately after a successful submission, bypassing the
 *     inline success message above. Set from CMS → Instances → this
 *     module's config → "Redirect after submit".
 */
export default function CapturePropertyFormModule({ config = {} }) {
  const {
    heading = "List Your Property",
    subheading = "Tell us about your property — we'll email you a link to finish setting up your listing.",
    submit_button_text = "Submit Property",
    success_heading = "Thanks — check your inbox",
    success_message = "We've emailed you a link to set a password and finish publishing your listing. If you submitted more than one property, one link unlocks all of them.",
    success_message_2 = "No rush — you can register whenever suits you and pick up your listing right where you left off.",
    redirect_slug = "",
    form_bg_color = "",
    post_submit_cta_copy = "",
    post_submit_cta_url = "",
  } = config;

  const [schemaFields, setSchemaFields] = useState([]);
  const [groupOrder, setGroupOrder] = useState(["contact", "property", "address", "price", "agent", "assets", "features", "seo", "listing_status", "property_status", "other"]);
  const [groupLabels, setGroupLabels] = useState(SECTION_LABELS);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Populated from the submit response when a valuation was run — shown on
  // the thank-you screen after the form is hidden, not before submitting.
  const [submittedEstimate, setSubmittedEstimate] = useState(null);

  // Name/email/phone pre-fill only — no sign-in required to use this
  // form. If the visitor happens to already be a signed-in agent
  // (property-admin, e.g. on /createlisting) or a signed-in shopper, we
  // quietly pre-fill their contact details from that session; anonymous
  // visitors just type them in manually below.
  const { shopper } = useShopperAuth();
  const [agentSignedIn, setAgentSignedIn] = useState(false);

  // Postcode -> address lookup (getAddress.io-compatible proxy, see
  // routes/address_lookup.py). `addressSuggestions` holds the autocomplete
  // list shown under the postcode field until the visitor picks one.
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState("");
  // Agent-only auto-save: as soon as a signed-in agent has typed enough to
  // identify the property (postcode or title), it's created as a draft on
  // their account so it shows up in My Properties right away — further
  // field changes then debounce-PATCH the same draft. Anonymous visitors
  // get a 401 on the first attempt, which flips `isAgentRef` off so we
  // never try again for the rest of this session (normal one-shot Submit
  // flow applies to them, untouched).
  const [draftPropertyId, setDraftPropertyId] = useState(null);
  const [draftSaveStatus, setDraftSaveStatus] = useState("idle"); // idle | saving | saved
  const isAgentRef = useRef(true);
  const draftTimerRef = useRef(null);
  // Debug: `?show=yes` in the URL prints each field's internal CMS key next
  // to its label, for admin QA when checking field mappings.
  const showDbNames = useShowDbFieldNames();

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/public/list-property/schema`).catch(() => ({ data: { fields: [] } })),
      axios.get(`${API}/public/cms-group-order`).catch(() => ({ data: { order: null } })),
      axios.get(`${API}/public/cms-field-groups`).catch(() => ({ data: { labels: null } })),
    ]).then(([schemaResp, orderResp, groupsResp]) => {
      if (cancelled) return;
      setSchemaFields(schemaResp.data?.fields || []);
      if (Array.isArray(orderResp.data?.order) && orderResp.data.order.length) {
        // Admin-configured group order doesn't carry "contact" (that's a
        // fixed, always-first block on this form) — keep it pinned first.
        setGroupOrder(["contact", ...orderResp.data.order.filter((k) => k !== "contact")]);
      }
      if (groupsResp.data?.labels) {
        setGroupLabels((prev) => ({ ...prev, ...groupsResp.data.labels }));
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Signed-in agent visiting this form (e.g. on an agent-gated page) — pull
  // their profile from the SSO session and pre-fill Name/Email/Phone so
  // they don't have to retype their own details. Anonymous visitors get a
  // 401 here, silently ignored (contact fields just stay blank as before).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/property-admin/me`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        setAgentSignedIn(true);
        const agent = data?.agent;
        const prefill = agent
          ? { name: agent.contact_name || agent.name || "", email: agent.email || "", phone: agent.phone || "" }
          : { name: "", email: data?.username || "", phone: "" };
        setContact((prev) => ({
          name: prev.name || prefill.name,
          email: prev.email || prefill.email,
          phone: prev.phone || prefill.phone,
        }));
      })
      .catch(() => { if (!cancelled) setAgentSignedIn(false); }); // not signed in as an agent — nothing to prefill
    return () => { cancelled = true; };
  }, []);

  // Anonymous visitor who just signed in via the Shopper Google SSO gate
  // below — pre-fill their name/email from the verified Google profile so
  // the account/report/email all use a real, already-confirmed identity.
  useEffect(() => {
    if (!shopper) return;
    setContact((prev) => ({
      name: prev.name || shopper.name || "",
      email: prev.email || shopper.email || "",
      phone: prev.phone,
    }));
  }, [shopper]);

  const sections = useMemo(() => {
    const bySection = {};
    schemaFields.forEach((f) => {
      const key = f.section || "property";
      if (!bySection[key]) bySection[key] = [];
      bySection[key].push(f);
    });
    // Fields within each group are sorted by their admin-configured
    // display_order (also the drag-reorder order in CMS → Fields).
    Object.values(bySection).forEach((fields) => {
      fields.sort((a, b) => (Number(a.display_order) || 0) - (Number(b.display_order) || 0));
    });
    // Admin-configured group order (CMS → Fields), contact pinned first.
    const order = groupOrder;
    const keys = [...order.filter((k) => bySection[k]), ...Object.keys(bySection).filter((k) => !order.includes(k))];
    return keys.map((key) => ({ key, label: groupLabels[key] || key, fields: bySection[key] }));
  }, [schemaFields, groupOrder, groupLabels]);

  const setFieldValue = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  // Mirror the address fields into a "capturedpropertydetails" cookie as
  // the visitor fills them in — the House Prices tool reads this back to
  // pre-fill its postcode box for the same visitor. Merged (not
  // overwritten), and empty fields are left out of the patch entirely —
  // otherwise re-visiting this form with only a postcode typed so far
  // would blank out street/town already saved from an earlier visit.
  useEffect(() => {
    const details = {
      postcode: values.postcode || "",
      house_name_number: values.cms_House_Name_Number || "",
      street_name: values.street_name || "",
      town_city: values.cms_town_city || "",
      display_address: values.display_address || "",
    };
    const nonEmpty = Object.fromEntries(Object.entries(details).filter(([, v]) => v));
    if (Object.keys(nonEmpty).length > 0) {
      mergeCapturedPropertyDetails(nonEmpty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.postcode, values.cms_House_Name_Number, values.street_name, values.cms_town_city, values.display_address]);

  // UK postcode splits into an "outward" (area+district, e.g. "SW1A") and
  // "inward" (sector+unit, e.g. "1AA") code — usually separated by a space.
  // Falls back to the last 3 characters as inward when there's no space.
  const splitPostcode = (raw) => {
    const trimmed = (raw || "").trim().toUpperCase();
    if (!trimmed) return { first: "", last: "" };
    const parts = trimmed.split(/\s+/);
    if (parts.length >= 2) return { first: parts[0], last: parts[parts.length - 1] };
    if (trimmed.length > 3) return { first: trimmed.slice(0, -3), last: trimmed.slice(-3) };
    return { first: trimmed, last: "" };
  };

  // Postcode field auto-populates the hidden cms_postcode_1 (outward) /
  // cms_postcode_2 (inward) fields used by the Rightmove feed — these
  // aren't shown on this public form but travel with the submission.
  const onPostcodeChange = (value) => {
    const { first, last } = splitPostcode(value);
    setValues((prev) => ({ ...prev, postcode: value, cms_postcode_1: first, cms_postcode_2: last }));
  };

  // Reverse of the mirror above: if the visitor checked House Prices
  // first and a postcode is sitting in the cookie, pre-fill this form's
  // postcode field with it — once only, and only while it's still empty.
  const hasPrefilledPostcodeRef = useRef(false);
  useEffect(() => {
    if (hasPrefilledPostcodeRef.current) return;
    if (values.postcode) { hasPrefilledPostcodeRef.current = true; return; }
    const captured = getCapturedPropertyDetails();
    if (captured?.postcode) {
      hasPrefilledPostcodeRef.current = true;
      onPostcodeChange(captured.postcode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.postcode]);

  // Title auto-builds from "<beds> <property_type> in <cms_town_city>"
  // whenever any of those three change — gaps (not yet filled in) are
  // skipped rather than leaving literal blanks.
  const TITLE_TRIGGER_KEYS = ["beds", "property_type", "cms_town_city"];
  const buildAutoTitle = (v) => {
    const bedsPart = v.beds ? `${v.beds} Bedroom` : "";
    const typePart = (v.property_type || "").trim();
    const town = (v.cms_town_city || "").trim();
    let title = [bedsPart, typePart].filter(Boolean).join(" ");
    if (town) title = title ? `${title} in ${town}` : `In ${town}`;
    return title;
  };

  // Display Address auto-builds "<street_name>, <cms_town_city>" once BOTH
  // are filled in — left untouched (not cleared) while either is still
  // empty, so a manual edit before that point isn't clobbered.
  const DISPLAY_ADDRESS_TRIGGER_KEYS = ["street_name", "cms_town_city"];
  const buildAutoDisplayAddress = (v) => {
    const street = (v.street_name || "").trim();
    const town = (v.cms_town_city || "").trim();
    return street && town ? `${street}, ${town}` : null;
  };

  const handleFieldChange = (key, value) => {
    if (key === "postcode") return onPostcodeChange(value);
    setValues((prev) => {
      const next = { ...prev, [key]: value };
      if (TITLE_TRIGGER_KEYS.includes(key)) next.title = buildAutoTitle(next);
      if (DISPLAY_ADDRESS_TRIGGER_KEYS.includes(key)) {
        const addr = buildAutoDisplayAddress(next);
        if (addr !== null) next.display_address = addr;
      }
      return next;
    });
  };

  // "Find address" — autocompletes the typed postcode against the UK
  // address database (proxied server-side, see routes/address_lookup.py)
  // and shows a pickable suggestion list under the postcode field.
  const lookupPostcodeAddresses = async () => {
    const postcode = (values.postcode || "").trim();
    if (!postcode) {
      setAddressLookupError("Enter a postcode first");
      return;
    }
    setAddressLookupError("");
    setAddressSuggestions([]);
    setAddressLookupLoading(true);
    try {
      const { data } = await axios.get(`${API}/public/address-lookup/autocomplete`, {
        params: { postcode },
      });
      const suggestions = data?.suggestions || [];
      if (!suggestions.length) setAddressLookupError("No addresses found for that postcode");
      setAddressSuggestions(suggestions);
    } catch (err) {
      setAddressLookupError(err.response?.data?.detail || "Address lookup failed");
    } finally {
      setAddressLookupLoading(false);
    }
  };

  // Selecting a suggestion resolves its full address and populates the
  // house name/number, street and town fields (plus the auto-built title
  // and display address), leaving them editable afterwards.
  const selectPostcodeAddress = async (suggestion) => {
    setAddressLookupLoading(true);
    try {
      const { data } = await axios.get(`${API}/public/address-lookup/get/${suggestion.id}`);
      const houseNameNumber = [data.building_number, data.building_name].filter(Boolean).join(" ").trim();
      const streetName = data.thoroughfare || data.line_1 || "";
      const townCity = data.town_or_city || "";
      const { first, last } = splitPostcode(data.postcode || values.postcode || "");
      setValues((prev) => {
        const next = {
          ...prev,
          cms_House_Name_Number: houseNameNumber || prev.cms_House_Name_Number,
          street_name: streetName || prev.street_name,
          cms_town_city: townCity || prev.cms_town_city,
          postcode: data.postcode || prev.postcode,
          cms_postcode_1: first,
          cms_postcode_2: last,
        };
        next.title = buildAutoTitle(next);
        const addr = buildAutoDisplayAddress(next);
        if (addr !== null) next.display_address = addr;
        return next;
      });
      setAddressSuggestions([]);
      toast.success("Address filled in — double-check the details below");
    } catch (err) {
      setAddressLookupError(err.response?.data?.detail || "Could not retrieve that address");
    } finally {
      setAddressLookupLoading(false);
    }
  };

  const buildFieldsPayload = () => {
    const fields = {};
    Object.entries(values).forEach(([k, v]) => {
      const meta = schemaFields.find((f) => f.key === k);
      if (meta?.type === "array" && typeof v === "string") {
        fields[k] = v.split(",").map((s) => s.trim()).filter(Boolean);
      } else if (meta?.type === "number" && v !== "") {
        fields[k] = Number(v);
      } else {
        fields[k] = v;
      }
    });
    return fields;
  };

  // Agent-only auto-save (see state comment above): debounced so we're not
  // firing a request on every keystroke.
  useEffect(() => {
    if (!isAgentRef.current || submitted) return;
    const hasIdentifier = (values.postcode || values.title || "").trim();
    if (!draftPropertyId && !hasIdentifier) return;
    if (Object.keys(values).length === 0) return;

    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(async () => {
      const fields = buildFieldsPayload();
      const vendorPayload = { vendor_name: contact.name, vendor_email: contact.email, vendor_phone: contact.phone };
      setDraftSaveStatus("saving");
      try {
        if (!draftPropertyId) {
          const { data } = await axios.post(`${API}/list-property/draft`, { fields, ...vendorPayload }, { withCredentials: true });
          setDraftPropertyId(data.property_id);
        } else {
          await axios.patch(`${API}/list-property/draft/${draftPropertyId}`, { fields, ...vendorPayload }, { withCredentials: true });
        }
        setDraftSaveStatus("saved");
      } catch (err) {
        if (err.response?.status === 401) {
          // Not signed in as an agent — anonymous visitors keep the
          // original one-shot Submit flow, so stop trying for the rest
          // of this session.
          isAgentRef.current = false;
          setDraftSaveStatus("idle");
        } else {
          console.error("Draft auto-save failed:", err);
          setDraftSaveStatus("idle");
        }
      }
    }, 1200);
    return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, contact]);

  const renderField = (field) => {
    const val = values[field.key] ?? "";
    if (field.key === "postcode") {
      return (
        <div>
          <div className="flex gap-2">
            <Input
              type="text"
              value={val}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupPostcodeAddresses(); } }}
              placeholder="e.g. SW1A 1AA"
              data-testid={`capture-property-field-${field.key}`}
            />
            <Button
              type="button"
              variant="outline"
              onClick={lookupPostcodeAddresses}
              disabled={addressLookupLoading}
              data-testid="capture-property-find-address-btn"
            >
              {addressLookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find address"}
            </Button>
          </div>
          {addressLookupError && (
            <p className="text-xs text-destructive mt-1" data-testid="capture-property-address-lookup-error">
              {addressLookupError}
            </p>
          )}
          {addressSuggestions.length > 0 && (
            <div className="mt-1 border border-input rounded max-h-48 overflow-y-auto" data-testid="capture-property-address-suggestions">
              {addressSuggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => selectPostcodeAddress(s)}
                  className="block w-full text-left text-sm px-3 py-2 hover:bg-muted border-b border-input last:border-b-0"
                  data-testid={`capture-property-address-suggestion-${s.id}`}
                >
                  {s.address}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (field.key === "features") {
      return (
        <FeatureChipsInput
          value={val}
          onChange={(next) => setFieldValue(field.key, next)}
          testIdPrefix="capture-property-features"
        />
      );
    }
    if (field.allowed_values && field.allowed_values.length > 0) {
      return (
        <select
          value={val}
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          className="w-full h-10 px-3 border border-input rounded text-sm bg-background"
          data-testid={`capture-property-field-${field.key}`}
        >
          <option value="">Select…</option>
          {field.allowed_values.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      );
    }
    if (field.type === "boolean") {
      return (
        <label className="flex items-center gap-2 text-sm cursor-pointer h-10">
          <input
            type="checkbox"
            checked={!!val}
            onChange={(e) => setFieldValue(field.key, e.target.checked)}
            data-testid={`capture-property-field-${field.key}`}
          />
          Yes
        </label>
      );
    }
    if (field.type === "number") {
      return (
        <Input
          type="number"
          value={val}
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          data-testid={`capture-property-field-${field.key}`}
        />
      );
    }
    if (field.key === "description" || field.type === "array") {
      return (
        <textarea
          rows={field.key === "description" ? 4 : 2}
          value={val}
          onChange={(e) => setFieldValue(field.key, e.target.value)}
          placeholder={field.type === "array" ? "Comma-separated" : ""}
          className="w-full px-3 py-2 border border-input rounded text-sm"
          data-testid={`capture-property-field-${field.key}`}
        />
      );
    }
    return (
      <Input
        type="text"
        value={val}
        onChange={(e) => handleFieldChange(field.key, e.target.value)}
        data-testid={`capture-property-field-${field.key}`}
      />
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.name.trim() || !contact.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    // Stop the agent-draft auto-save from firing a stray request after
    // we've started the real submission — a pending debounce timer that
    // fired right as a shopper got claimed (and so picked up their
    // brand-new property_admin_token cookie) was creating a second,
    // duplicate property.
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    isAgentRef.current = false;
    try {
      setSubmitting(true);
      const fields = buildFieldsPayload();
      if (draftPropertyId) {
        // Already auto-saved as a draft on the agent's account — finalize
        // with one more save instead of creating a second property; this
        // still runs the full tenant-account + valuation + report + email
        // pipeline for the vendor, same as a fresh public submission.
        const { data } = await axios.post(`${API}/list-property/draft/${draftPropertyId}/finalize`, {
          fields,
          vendor_name: contact.name,
          vendor_email: contact.email,
          vendor_phone: contact.phone,
        }, { withCredentials: true });
        if (data.estimated_low != null && data.estimated_high != null) {
          setSubmittedEstimate({ low: data.estimated_low, high: data.estimated_high, fromCache: !!data.from_cache });
        }
      } else {
        const { data } = await axios.post(`${API}/list-property/submit`, {
          name: contact.name.trim(),
          email: contact.email.trim(),
          phone: contact.phone.trim(),
          vendor_name: contact.name.trim(),
          vendor_email: contact.email.trim(),
          vendor_phone: contact.phone.trim(),
          fields,
        }, { withCredentials: true });
        if (data.estimated_low != null && data.estimated_high != null) {
          setSubmittedEstimate({ low: data.estimated_low, high: data.estimated_high, fromCache: !!data.from_cache });
        }
        // Clear the form fields now that the submission has been saved
        // server-side — nothing left to lose if the visitor lands back on
        // the (now thank-you) screen, and a stray re-submit on back/forward
        // navigation would otherwise resend stale values.
        setValues({});
        setContact({ name: "", email: "", phone: "" });
      }
      // Admin-configured post-submit redirect (CMS instance config,
      // "Redirect after submit") takes the visitor straight to that page.
      // Uses a real browser navigation (not client-side routing) so a
      // leading "/" always means "from the site root", with no ambiguity
      // from whatever route pattern this form happens to be rendered under
      // (e.g. the "/:slug" catch-all) — and it works the same for a full
      // external URL (e.g. https://example.com).
      // Otherwise fall back to the inline success message below.
      if (redirect_slug) {
        window.location.href = redirect_slug;
        return;
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-16 px-6" data-testid="capture-property-form-success">
        <div className="max-w-lg mx-auto text-center space-y-4">
          <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
          {draftPropertyId ? (
            <>
              {success_heading && <h2>{success_heading}</h2>}
              {success_message && <p className="text-muted-foreground">{success_message}</p>}
              <Button asChild className="mt-2" data-testid="capture-property-view-listings-btn">
                <a
                  href={post_submit_cta_url || "/property-admin/dashboard"}
                  target={post_submit_cta_url?.startsWith("http") ? "_blank" : undefined}
                  rel={post_submit_cta_url?.startsWith("http") ? "noopener noreferrer" : undefined}
                >
                  {post_submit_cta_copy || "View My Properties"}
                </a>
              </Button>
            </>
          ) : (
            <>
              {submittedEstimate && (
                <div className="border rounded-md p-4 bg-muted/30" data-testid="capture-property-submitted-estimate">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Estimated Value</p>
                  <p className="text-2xl font-semibold mt-1">
                    £{submittedEstimate.low.toLocaleString()} – £{submittedEstimate.high.toLocaleString()}
                  </p>
                  <p
                    className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1"
                    data-testid="capture-property-estimate-cache-indicator"
                  >
                    {submittedEstimate.fromCache ? (
                      <><Zap className="h-3 w-3" /> Instant result — reused an estimate already run for this address</>
                    ) : (
                      <><Sparkles className="h-3 w-3" /> Freshly calculated from live sold-price data</>
                    )}
                  </p>
                </div>
              )}
              {success_heading && (
                <h2 data-testid="capture-property-valuation-sent-heading">
                  {success_heading}
                </h2>
              )}
              {success_message && (
                <p className="text-muted-foreground">{success_message}</p>
              )}
              {success_message_2 && (
                <p className="text-muted-foreground pt-2 opacity-80" data-testid="capture-property-success-message-2">
                  {success_message_2}
                </p>
              )}
              {post_submit_cta_copy && post_submit_cta_url && (
                <div className="pt-4" data-testid="capture-property-post-submit-cta">
                  <Button
                    asChild
                    data-testid="capture-property-post-submit-cta-btn"
                  >
                    <a
                      href={post_submit_cta_url}
                      target={post_submit_cta_url.startsWith("http") ? "_blank" : undefined}
                      rel={post_submit_cta_url.startsWith("http") ? "noopener noreferrer" : undefined}
                    >
                      {post_submit_cta_copy}
                    </a>
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    );
  }

  // No sign-in required — the form renders directly for every visitor.
  // `shopper`/`agentSignedIn` above are used only to silently pre-fill
  // Name/Email/Phone when a session already exists.

  return (
    <section className="py-12 px-6" data-testid="capture-property-form-module">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="font-serif text-3xl font-medium">{heading}</h2>
          {subheading && <p className="text-sm text-muted-foreground mt-2">{subheading}</p>}
          {draftPropertyId && (
            <p
              className="text-xs text-emerald-700 mt-3 flex items-center justify-center gap-1"
              data-testid="capture-property-draft-status"
            >
              {draftSaveStatus === "saving" ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> Saving draft…</>
              ) : (
                <>
                  <CheckCircle2 className="h-3 w-3" /> Draft saved — find it anytime in{" "}
                  <a href="/property-admin/dashboard" className="underline font-medium">My Properties</a>
                </>
              )}
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6 bg-background border rounded p-6" style={form_bg_color ? { backgroundColor: form_bg_color } : undefined} data-testid="capture-property-form">
            <div className="space-y-3">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Your Contact Details</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Full name * <DbFieldBadge name="name" enabled={showDbNames} /></Label>
                  <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} required data-testid="capture-property-contact-name" />
                </div>
                <div>
                  <Label>Email * <DbFieldBadge name="email" enabled={showDbNames} /></Label>
                  <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} required data-testid="capture-property-contact-email" />
                </div>
              </div>
              <div>
                <Label>Phone <DbFieldBadge name="phone" enabled={showDbNames} /></Label>
                <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="capture-property-contact-phone" />
              </div>
            </div>

            {sections.map((section) => (
              <div key={section.key} className="space-y-3 pt-4 border-t" data-testid={`capture-property-section-${section.key}`}>
                <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">{section.label}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {section.fields.map((field) => (
                    <div key={field.key} className={field.key === "description" || field.key === "features" || field.type === "array" ? "sm:col-span-2" : ""}>
                      <Label>{humaniseLabel(field)} <DbFieldBadge name={field.key} enabled={showDbNames} /></Label>
                      {renderField(field)}
                      {field.info && <p className="text-xs text-muted-foreground mt-1">{field.info}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {sections.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-2" data-testid="capture-property-no-fields">
                No property fields have been enabled for this form yet.
              </p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full"
              data-testid="capture-property-submit-btn"
            >
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting…</> : submit_button_text}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}
